/**
 * TimeBlock - Individual time block in the week planner grid
 *
 * Features:
 * - Subject name with color coding
 * - Curriculum outcome chips
 * - Resource count indicator
 * - Click handler for editing
 * - Empty state for adding new blocks
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, BookOpen, Target } from "lucide-react";
import {
  type TimeBlock as TimeBlockType,
  type CurriculumOutcome,
  SUBJECT_COLORS,
  SUBJECT_DOT_COLORS,
  formatTime,
} from "./types";

// ============================================================================
// Types
// ============================================================================

export interface TimeBlockProps {
  /** Time block data (null for empty slot) */
  block: TimeBlockType | null;
  /** Start time for empty slots */
  startTime?: string;
  /** End time for empty slots */
  endTime?: string;
  /** Map of outcome IDs to outcome data */
  outcomesMap?: Map<string, CurriculumOutcome>;
  /** Called when block is clicked */
  onClick?: () => void;
  /** Whether this block is currently selected */
  isSelected?: boolean;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Sub-Components
// ============================================================================

interface OutcomeChipProps {
  outcome: CurriculumOutcome;
}

function OutcomeChip({ outcome }: OutcomeChipProps) {
  // Get short subject abbreviation
  const subjectAbbr = useMemo(() => {
    const abbrevMap: Record<string, string> = {
      "Social Studies": "SS",
      Mathematics: "Math",
      Science: "Sci",
      "English Language Arts": "ELA",
    };
    return abbrevMap[outcome.subject] || outcome.subject.slice(0, 3);
  }, [outcome.subject]);

  // Extract grade number
  const gradeNum = outcome.grade.replace("Grade ", "").replace("Kindergarten", "K");

  return (
    <Badge
      variant="secondary"
      className="text-[10px] px-1.5 py-0 h-5 font-normal truncate max-w-full"
      title={outcome.outcome_text}
    >
      <Target className="h-2.5 w-2.5 mr-1 flex-shrink-0" />
      <span className="truncate">
        {subjectAbbr} {gradeNum}
      </span>
    </Badge>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function TimeBlock({
  block,
  startTime,
  endTime,
  outcomesMap,
  onClick,
  isSelected,
  className,
}: TimeBlockProps) {
  // Get outcomes for this block
  const outcomes = useMemo(() => {
    if (!block || !outcomesMap) return [];
    return block.outcomeIds
      .map((id) => outcomesMap.get(id))
      .filter((o): o is CurriculumOutcome => o !== undefined)
      .slice(0, 2); // Show max 2 chips
  }, [block, outcomesMap]);

  // Count additional outcomes
  const additionalOutcomes = useMemo(() => {
    if (!block) return 0;
    return Math.max(0, block.outcomeIds.length - 2);
  }, [block]);

  // Count resources
  const resourceCount = block?.resources?.length ?? 0;

  // Empty state
  if (!block) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "w-full h-full min-h-[60px] sm:min-h-[80px] p-2",
          "border border-dashed border-muted-foreground/30 rounded-md",
          "flex items-center justify-center",
          "text-muted-foreground/50 hover:text-muted-foreground",
          "hover:border-muted-foreground/50 hover:bg-muted/30",
          "transition-colors cursor-pointer",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          className
        )}
        aria-label={`Add block at ${startTime ? formatTime(startTime) : "this time"}`}
      >
        <Plus className="h-5 w-5" />
      </button>
    );
  }

  // Subject color classes
  const subjectColorClass = block.subject
    ? SUBJECT_COLORS[block.subject]
    : "bg-muted border-border text-muted-foreground";

  const dotColorClass = block.subject
    ? SUBJECT_DOT_COLORS[block.subject]
    : "bg-muted-foreground";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full h-full min-h-[60px] sm:min-h-[80px] p-2 text-left",
        "border rounded-md",
        subjectColorClass,
        "hover:brightness-95 transition-all cursor-pointer",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        isSelected && "ring-2 ring-primary ring-offset-2",
        className
      )}
      aria-label={`${block.subject || "Untitled"} block: ${block.title || "No title"}`}
    >
      <div className="flex flex-col h-full gap-1">
        {/* Subject + Title */}
        <div className="flex items-start gap-1.5">
          <div
            className={cn("w-2 h-2 rounded-full mt-1 flex-shrink-0", dotColorClass)}
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">
              {block.subject || "Untitled"}
            </div>
            {block.title && (
              <div className="text-xs opacity-80 truncate">{block.title}</div>
            )}
          </div>
        </div>

        {/* Outcomes chips */}
        {outcomes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto">
            {outcomes.map((outcome) => (
              <OutcomeChip key={outcome.id} outcome={outcome} />
            ))}
            {additionalOutcomes > 0 && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 h-5 font-normal"
              >
                +{additionalOutcomes}
              </Badge>
            )}
          </div>
        )}

        {/* Resources indicator */}
        {resourceCount > 0 && (
          <div className="flex items-center gap-1 mt-auto">
            <FileText className="h-3 w-3 opacity-60" />
            <span className="text-[10px] opacity-60">
              {resourceCount} {resourceCount === 1 ? "resource" : "resources"}
            </span>
          </div>
        )}

        {/* Empty content indicator */}
        {outcomes.length === 0 && resourceCount === 0 && !block.notes && (
          <div className="flex items-center gap-1 mt-auto opacity-50">
            <BookOpen className="h-3 w-3" />
            <span className="text-[10px]">Click to add details</span>
          </div>
        )}
      </div>
    </button>
  );
}
